import React from 'react';
import { GamificationStatus } from '../../hooks/useGamification';

interface OnboardingChecklistProps {
    status: GamificationStatus | null;
    onDismiss?: () => void;
    compact?: boolean;
}

/**
 * Clean, minimal onboarding checklist widget
 * Shows user progress through key actions
 */
const OnboardingChecklist: React.FC<OnboardingChecklistProps> = ({
    status,
    onDismiss,
    compact = false
}) => {
    const [expanded, setExpanded] = React.useState(false);

    if (!status) return null;

    // Only show free actions for onboarding
    const onboardingActions = status.actions.filter(a => a.category === 'free');
    const completedCount = onboardingActions.filter(a => a.completed).length;
    const totalCount = onboardingActions.length;
    const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

    // If all completed, don't show
    if (completedCount >= totalCount) return null;

    return (
        <div
            style={{
                background: 'var(--bg-box)',
                borderRadius: '12px',
                padding: compact ? '16px' : '20px',
                boxShadow: '0 2px 8px var(--shadow-color)',
                border: '1px solid var(--border-color)',
            }}
        >
            {/* Welcome Banner */}
            <div style={{
                background: 'var(--bg-input)', // Replaces blue-50
                border: '1px solid var(--border-color)', // Replaces blue-100
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '16px',
                display: 'flex',
                gap: '10px',
                alignItems: 'start'
            }}>
                <div style={{ fontSize: '16px' }}>👋</div>
                <div>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-heading)', marginBottom: '2px' }}>
                        Bienvenue sur Kanea !
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-main)', lineHeight: '1.4' }}>
                        Complétez ces missions pour découvrir la plateforme et gagner vos premiers XP.
                    </div>
                </div>
            </div>

            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
            }}>
                <div>
                    <h3 style={{
                        margin: 0,
                        fontSize: '15px',
                        fontWeight: 600,
                        color: 'var(--text-heading)'
                    }}>
                        🎯 Découvrez Kanea
                    </h3>
                    <p style={{
                        margin: '4px 0 0',
                        fontSize: '12px',
                        color: 'var(--text-muted)'
                    }}>
                        {completedCount}/{totalCount} étapes • {status.total_xp} XP
                    </p>
                </div>

                {onDismiss && (
                    <button
                        onClick={onDismiss}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#94a3b8',
                            cursor: 'pointer',
                            padding: '4px',
                            fontSize: '16px',
                        }}
                        title="Masquer"
                    >
                        ✕
                    </button>
                )}
            </div>

            {/* Progress bar */}
            <div
                style={{
                    height: '4px',
                    background: 'var(--bg-input)',
                    borderRadius: '2px',
                    marginBottom: '16px',
                    overflow: 'hidden',
                }}
            >
                <div
                    style={{
                        height: '100%',
                        width: `${progressPercent}%`,
                        background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: '2px',
                        transition: 'width 0.3s ease',
                    }}
                />
            </div>

            {/* Actions list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {onboardingActions.slice(0, (compact && !expanded) ? 5 : 10).map((action) => (
                    <div
                        key={action.code}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '8px 0',
                            borderBottom: '1px solid var(--border-color)',
                            opacity: action.completed ? 0.6 : 1,
                        }}
                    >
                        {/* Checkbox */}
                        <div
                            style={{
                                width: '20px',
                                height: '20px',
                                borderRadius: '50%',
                                border: action.completed
                                    ? 'none'
                                    : '2px solid var(--border-color)',
                                background: action.completed
                                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                    : 'transparent',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            {action.completed && (
                                <span style={{ color: 'white', fontSize: '12px' }}>✓</span>
                            )}
                        </div>

                        {/* Action name */}
                        <span
                            style={{
                                flex: 1,
                                fontSize: '13px',
                                color: action.completed ? 'var(--text-muted)' : 'var(--text-main)',
                                textDecoration: action.completed ? 'line-through' : 'none',
                            }}
                        >
                            {action.name}
                        </span>

                        {/* XP */}
                        <span
                            style={{
                                fontSize: '11px',
                                fontWeight: 600,
                                color: action.completed ? 'var(--text-muted)' : '#667eea',
                                background: action.completed ? 'var(--bg-input)' : 'var(--bg-hover)',
                                padding: '2px 8px',
                                borderRadius: '10px',
                            }}
                        >
                            +{action.xp_reward}
                        </span>
                    </div>
                ))}
            </div>

            {/* Show More Button */}
            {compact && onboardingActions.length > 5 && (
                <button
                    onClick={() => setExpanded(!expanded)}
                    style={{
                        width: '100%',
                        marginTop: '8px',
                        padding: '8px',
                        background: 'var(--bg-input)',
                        border: 'none',
                        borderRadius: '8px',
                        color: '#667eea',
                        fontSize: '13px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        textAlign: 'center'
                    }}
                >
                    {expanded ? '▲ Voir moins' : `▼ Voir la suite (${onboardingActions.length - 5})`}
                </button>
            )}

            {/* Level info */}
            <div
                style={{
                    marginTop: '16px',
                    padding: '12px',
                    background: 'var(--bg-input)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                }}
            >
                <div
                    style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '14px',
                    }}
                >
                    {status.level.level}
                </div>
                <div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-heading)' }}>
                        {status.level.title}
                    </div>
                    {status.level.xp_for_next_level && (
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                            {status.level.xp_for_next_level - status.total_xp} XP pour niveau suivant
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OnboardingChecklist;

